import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { PERIOD_PILLS, type CmpColumnDTO, type PeriodKey } from "@easyshop/shared";
import { ErrorState, Loading, cx } from "@/components/ui";
import { IosSegmented, MobileIcon, useMobileTitle } from "@/components/mobile/MobileUi";
import { useSession } from "@/hooks/useSession";
import { useSelectedBoutique } from "@/hooks/useSelectedBoutique";
import { usePeriod } from "@/hooks/usePeriod";
import { useUi } from "@/hooks/useUi";
import { useBoutiqueOverview, useDashboard, useMyPlanning } from "@/lib/queries";
import { UpcomingPromosGroup } from "@/components/planning/PromoChips";
import { ymd } from "@/components/planning/utils";
import { useBoutiqueActivity } from "@/layouts/MobileShell";
import { MobileDateNav } from "@/components/mobile/DateSheet";
import { HourlyBars, PeriodBars } from "@/components/mobile/HourlyBars";
import { BoutiqueCards } from "@/components/mobile/BoutiqueCards";
import { PositionCard, TopBoutiquesCard, TopProductsCard } from "@/components/Rankings";
import { cmpDateLabel, cmpShortLabel, periodShortLabel } from "@/lib/period-nav";
import { eur, eur2, margin, num, pct1 } from "@/lib/format";

const PERIOD_OPTIONS = (Object.keys(PERIOD_PILLS) as PeriodKey[]).map((key) => ({ key, label: PERIOD_PILLS[key] }));

/** Sélecteur de période des onglets (Ventes) : segmented control iOS + ‹ période ›. */
export function MobilePeriodSwitcher() {
  const { period, setPeriod } = usePeriod();
  return (
    <div className="flex flex-col gap-[6px]">
      <IosSegmented options={PERIOD_OPTIONS} value={period} onChange={setPeriod} />
      <MobileDateNav />
    </div>
  );
}

/** Sélecteur de période de l'accueil : pilule blanche (actif vert foncé) + ‹ période › en pastilles. */
function HomePeriodSwitcher() {
  const { period, setPeriod } = usePeriod();
  return (
    <div className="flex flex-col gap-2">
      <div className="flex rounded-full bg-white p-[3px] text-[14px] font-semibold">
        {PERIOD_OPTIONS.map((o) => (
          <button
            key={o.key}
            type="button"
            aria-pressed={period === o.key}
            onClick={() => setPeriod(o.key)}
            className={cx("flex-1 rounded-full py-[7px] text-center", period === o.key ? "bg-seg-active text-white" : "text-muted")}
          >
            {o.label}
          </button>
        ))}
      </div>
      <MobileDateNav round />
    </div>
  );
}

/** Grand titre iOS dans la page, avec son sur-titre ou son sous-titre. */
function LargeTitle({ over, title, sub, hideTitle }: { over?: string; title: string; sub?: string; hideTitle?: boolean }) {
  return (
    <header className="px-1">
      {over && <div className="truncate text-[13px] text-muted-2">{over}</div>}
      <h1 className={hideTitle ? "sr-only" : "truncate text-[28px] font-bold leading-[1.1] tracking-[-0.5px]"}>{title}</h1>
      {sub && <div className="truncate text-[13px] text-muted-2">{sub}</div>}
    </header>
  );
}

/** Anneau d'objectif de la carte héros (% atteint). */
function ObjectiveRing({ pct }: { pct: number }) {
  const r = 36;
  const c = 2 * Math.PI * r;
  const filled = Math.max(0, Math.min(1, pct / 100)) * c;
  return (
    <svg width="86" height="86" viewBox="0 0 86 86" className="flex-shrink-0" role="img" aria-label={`${Math.round(pct)} % de l'objectif`}>
      <circle cx="43" cy="43" r={r} fill="none" stroke="var(--hero-track)" strokeWidth="9" />
      <circle
        cx="43"
        cy="43"
        r={r}
        fill="none"
        stroke="var(--hero-ring)"
        strokeWidth="9"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${c}`}
        transform="rotate(-90 43 43)"
      />
      <text x="43" y="42" textAnchor="middle" fontSize="19" fontWeight="700" fill="#fff" className="tabular">
        {Math.round(pct)} %
      </text>
      <text x="43" y="58" textAnchor="middle" fontSize="10" fill="var(--hero-text)">
        objectif
      </text>
    </svg>
  );
}

/** Taille du grand chiffre : 44 px jusqu'à « 999 999 € », plus petit au-delà (CA annuel d'un réseau). */
const bigSize = (text: string) =>
  text.length <= 9 ? "text-[44px] tracking-[-1.4px]" : text.length <= 11 ? "text-[36px] tracking-[-1px]" : "text-[30px] tracking-[-0.8px]";

/** Une ligne du tableau des écarts : global, intégrées, franchises ou la boutique. */
interface HeroRow {
  label: string;
  dot: string;
  columns: CmpColumnDTO[];
}

/**
 * Carte héros : un seul grand chiffre (le CA), son écart en % et en €, l'anneau
 * d'objectif, le tableau des écarts M-1 / N-1 (CA de la période de référence
 * sous chaque %) et, en pied, tickets · panier · marge.
 */
function HeroCard({
  label,
  ca,
  prevCa,
  delta,
  cmpLabel,
  objectivePct,
  rows,
  tickets,
  panier,
  marge,
}: {
  label: string;
  ca: number;
  prevCa: number | null;
  delta: number | null;
  cmpLabel: string;
  objectivePct: number | null;
  rows: HeroRow[];
  tickets: number;
  panier: number;
  marge: number | null;
}) {
  const heads = rows[0]?.columns ?? [];
  const grid = { gridTemplateColumns: `1.2fr repeat(${heads.length}, minmax(0, 1fr))` };
  const up = (delta ?? 0) >= 0;
  const diff = prevCa != null ? ca - prevCa : null;
  return (
    <div className="rounded-[28px] bg-hero p-[22px] text-white">
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] text-hero-text">{label}</div>
          <div className={cx("mt-[6px] truncate font-bold leading-none tabular", bigSize(eur(ca)))}>{eur(ca)}</div>
          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-[6px]">
            {delta != null && diff != null && (
              <span className={cx("rounded-full px-[11px] py-[5px] text-[14px] font-semibold tabular", up ? "bg-hero-pill" : "bg-hero-pill-down")}>
                {up ? "▲" : "▼"} {pct1(delta)} · {diff >= 0 ? "+" : "−"}
                {eur(Math.abs(diff))}
              </span>
            )}
            <span className="text-[13px] text-hero-text">{delta != null ? `vs ${cmpLabel}` : "Pas de référence pour comparer"}</span>
          </div>
        </div>
        {objectivePct != null && <ObjectiveRing pct={objectivePct} />}
      </div>

      {heads.length > 0 && (
        <div className="mt-4 rounded-[16px] bg-hero-panel px-3 py-[6px]">
          <div className="grid gap-2 pb-1 pt-[6px] text-[11px] text-hero-faint" style={grid}>
            <span />
            {heads.map((c) => (
              <span key={c.id} title={c.title}>
                {c.head}
              </span>
            ))}
          </div>
          {rows.map((r, i) => (
            <div key={r.label} className={cx("grid items-center gap-2 py-[7px]", i > 0 && "border-t border-hero-line")} style={grid}>
              <span className={cx("flex min-w-0 items-center gap-[6px] text-[13px]", i === 0 ? "font-semibold" : "font-medium")}>
                <span className="h-[7px] w-[7px] flex-shrink-0 rounded-full" style={{ background: r.dot }} />
                <span className="truncate">{r.label}</span>
              </span>
              {r.columns.map((c) => (
                <span key={c.id} className="min-w-0" title={c.title}>
                  <span className={cx("block text-[14px] font-semibold tabular", !c.ca.hasData ? "text-hero-faint" : c.ca.up ? "text-hero-up" : "text-hero-down")}>
                    {c.ca.delta}
                  </span>
                  <span className="block truncate text-[11px] tabular text-hero-faint">{c.ca.value}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 grid grid-cols-3 gap-3 border-t border-hero-line pt-[14px]">
        {(
          [
            ["Tickets", num(tickets)],
            ["Panier", tickets ? eur2(panier) : "—"],
            ["Marge", margin(marge)],
          ] as const
        ).map(([k, v]) => (
          <div key={k} className="min-w-0">
            <div className="text-[12px] text-hero-faint">{k}</div>
            <div className="truncate text-[17px] font-semibold tabular">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Résumé IA : carte blanche, pastille « IA » et une ou deux phrases. */
function AiSummary({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-[10px] rounded-[22px] bg-white px-4 py-[14px]">
      <span className="flex-shrink-0 rounded-[7px] bg-accent-soft px-[7px] py-[3px] text-[12px] font-bold text-accent">IA</span>
      <span className="min-w-0 flex-1 text-[14px] leading-[1.4]">{text}</span>
    </div>
  );
}

/** Titre de l'histogramme hors vue Jour. */
const BARS_TITLE: Record<string, string> = { semaine: "Ventes par jour", mois: "Ventes par semaine", annee: "Ventes par mois" };

/** Tuile de la bande « Aujourd'hui » (lien facultatif). */
function TodayTile({ label, value, hint, to, tone }: { label: string; value: ReactNode; hint?: ReactNode; to?: string; tone?: "danger" }) {
  const body = (
    <>
      <div className="truncate text-[12px] text-muted">{label}</div>
      <div className={cx("mt-[3px] truncate text-[15px] font-semibold tracking-[-0.3px]", tone === "danger" && "text-danger")}>{value}</div>
      {hint && <div className="mt-px truncate text-[12px] text-faint">{hint}</div>}
    </>
  );
  const cls = "min-h-[72px] min-w-0 rounded-[18px] bg-white px-[10px] py-[10px]";
  return to ? (
    <Link to={to} className={cls}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/** « Aujourd'hui » (boutique unique) : mon service du jour, tâches ouvertes / en retard, messages non lus. */
function TodayStrip() {
  const { can } = useSession();
  const mine = useMyPlanning();
  const { unreadHere, unread, openTasks, lateTasks } = useBoutiqueActivity();
  const today = ymd(new Date());
  const shifts = (mine.data?.shifts ?? []).filter((s) => s.date === today);
  const hasStaff = (mine.data?.staffMemberIds.length ?? 0) > 0;
  const tiles: ReactNode[] = [];
  if (hasStaff) {
    tiles.push(
      <TodayTile
        key="shift"
        label="Mon service"
        value={shifts.length ? `${shifts[0].start}–${shifts[0].end}` : "Repos"}
        hint={shifts.length > 1 ? `+${shifts.length - 1} créneau${shifts.length > 2 ? "x" : ""}` : shifts[0]?.city}
        to={can("planning.read") ? "/m/planning" : undefined}
      />,
    );
  }
  if (can("messages.read")) {
    tiles.push(
      <TodayTile
        key="tasks"
        label="Tâches"
        value={lateTasks ? `${lateTasks} en retard` : `${openTasks} à faire`}
        hint={lateTasks ? `${openTasks} à faire` : openTasks ? "ouvertes" : "rien en attente"}
        tone={lateTasks ? "danger" : undefined}
        to="/m/taches"
      />,
      <TodayTile
        key="messages"
        label="Messages"
        value={`${unreadHere} non lu${unreadHere > 1 ? "s" : ""}`}
        hint={unread > unreadHere ? `${unread - unreadHere} ailleurs` : "cette boutique"}
        to="/m/messages"
      />,
    );
  }
  if (tiles.length === 0) return null;
  return <div className={cx("grid gap-2", tiles.length === 3 ? "grid-cols-3" : tiles.length === 2 ? "grid-cols-2" : "grid-cols-1")}>{tiles}</div>;
}

/** Clé d'une période : l'histogramme repart de son créneau par défaut quand elle change. */
const periodKey = (p: { period: string; date: string | null; range: { from: string; to: string } | null }) =>
  `${p.period}:${p.date ?? ""}:${p.range?.from ?? ""}:${p.range?.to ?? ""}`;

/** « 1ʳᵉ », « 2ᵉ »… */
const ordinal = (n: number) => (n === 1 ? "1ʳᵉ" : `${n}ᵉ`);

/**
 * Accueil mobile (handoff_v3, écran 3a). Plusieurs boutiques : carte héros du
 * périmètre, ventes par heure, résumé, tops, puis les cartes boutiques ; un
 * toucher ouvre la page boutique (3b). Une seule boutique : sa page directement.
 */
export function MobileHome() {
  const { me } = useSession();
  const { select } = useSelectedBoutique();
  const { openProduct } = useUi();
  const multiple = me.boutiques.length > 1;
  const [focusId, setFocusId] = useState<string | null>(null);
  const dash = useDashboard();
  const period = usePeriod();
  const d = dash.data;
  const focused = multiple && focusId ? me.boutiques.find((b) => b.id === focusId) : undefined;

  // Le titre est dans la page ; la barre ne garde que le retour.
  useMobileTitle(focused ? focused.city : null, {
    back: {
      label: me.networkWide ? "Réseau" : "Boutiques",
      onClick: () => setFocusId(null),
    },
  });

  const open = (id: string) => {
    select(id);
    setFocusId(id);
    window.scrollTo({ top: 0 });
  };

  if (focused) return <BoutiquePage id={focused.id} />;
  if (!multiple) {
    return me.boutiques[0] ? <BoutiquePage id={me.boutiques[0].id} today /> : <LargeTitle title="Accueil" sub="Aucune boutique." hideTitle />;
  }

  const over = me.networkWide
    ? `${me.organization?.name ?? "Réseau"} · ${me.boutiques.length} boutiques`
    : me.boutiques.length > 4
      ? `${me.boutiques.length} boutiques`
      : me.boutiques.map((b) => b.city).join(" · ");
  const cmpShort = cmpShortLabel(period);
  const barsKey = periodKey(period);
  const hasInt = (d?.boutiques ?? []).some((b) => b.type === "INTEGRATED");
  const hasFra = (d?.boutiques ?? []).some((b) => b.type === "FRANCHISE");
  const mixed = hasInt && hasFra;

  return (
    <div className="flex flex-col gap-[14px]">
      {/* Pas de grand titre « Accueil » : l'onglet du bas l'indique déjà. */}
      <LargeTitle over={over} title="Accueil" hideTitle />
      <HomePeriodSwitcher />

      {dash.isLoading && <Loading />}
      {dash.error && <ErrorState error={dash.error} />}
      {d && (
        <>
          <HeroCard
            label={me.networkWide ? "CA du réseau" : "CA de mes boutiques"}
            ca={d.kpis.ca}
            prevCa={d.kpis.prevCa}
            delta={d.kpis.delta}
            cmpLabel={cmpDateLabel(period)}
            objectivePct={d.kpis.objectivePct}
            rows={[
              { label: "Global", dot: "#fff", columns: d.cmpColumns },
              ...(mixed
                ? [
                    { label: "Intégrées", dot: "var(--hero-ring)", columns: d.cmpColumnsIntegrated },
                    { label: "Franchises", dot: "oklch(0.78 0.1 300)", columns: d.cmpColumnsFranchise },
                  ]
                : []),
            ]}
            tickets={d.kpis.tickets}
            panier={d.kpis.panier}
            marge={d.kpis.marge}
          />

          {d.hourly ? (
            <HourlyBars key={barsKey} hourly={d.hourly} mode={mixed ? "segments" : "cmp"} currentLabel={periodShortLabel(period)} cmpLabel={cmpDateLabel(period)} />
          ) : (
            <PeriodBars key={barsKey} chart={d.chart} title={BARS_TITLE[period.period] ?? "Ventes"} currentLabel={periodShortLabel(period)} cmpLabel={cmpShort} />
          )}

          <AiSummary text={d.summary.text} />

          <TopBoutiquesCard title="Top boutiques" items={d.topIntegrated} kind="INTEGRATED" cmpLabel={cmpShort} onSelect={open} />
          <TopBoutiquesCard title="Top franchises" items={d.topFranchise} kind="FRANCHISE" cmpLabel={cmpShort} onSelect={open} />
          <TopProductsCard products={d.topProducts} cmpLabel={cmpShort} onSelect={(p) => openProduct({ id: p.id, name: p.name, scope: "reseau" })} />

          <BoutiqueCards title={me.networkWide ? "Boutiques" : "Mes boutiques"} boutiques={d.boutiques} cmpLabel={cmpShort} onSelect={open} />
        </>
      )}
    </div>
  );
}

/**
 * Page boutique (écran 3b) : même sélecteur et même carte héros que l'accueil,
 * ventes par heure comparées à la journée de référence, position vs réseau,
 * top produits, promos. `today` : bande « Aujourd'hui » et résumé (boutique unique).
 */
function BoutiquePage({ id, today = false }: { id: string; today?: boolean }) {
  const { me } = useSession();
  const { openProduct } = useUi();
  const period = usePeriod();
  const ov = useBoutiqueOverview(id);
  const planning = useMyPlanning();
  const upcoming = (planning.data?.upcomingPromos ?? []).filter((p) => p.boutiqueIds.includes(id));
  const ref = me.boutiques.find((x) => x.id === id);
  const b = ov.data?.boutique;
  const type = (b ?? ref)?.type;
  const scope = me.networkWide ? "du réseau" : "de mes boutiques";
  const sub = [
    type === "FRANCHISE" ? "Franchise" : type === "INTEGRATED" ? "Succursale intégrée" : null,
    b && ov.data!.networkSize > 1 ? `${ordinal(b.rank)} ${scope}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const cmpShort = cmpShortLabel(period);
  const barsKey = periodKey(period);

  return (
    <div className="flex flex-col gap-[14px]">
      <LargeTitle title={ref?.city ?? b?.city ?? "Boutique"} sub={sub || undefined} />
      {today && <TodayStrip />}
      <HomePeriodSwitcher />

      {ov.isLoading && <Loading />}
      {ov.error && <ErrorState error={ov.error} />}
      {ov.data && b && (
        <>
          {b.promosToday.length > 0 && (
            <div className="flex min-h-[44px] items-center gap-2 rounded-[18px] bg-accent px-4 py-[10px] text-[15px] font-semibold text-white">
              <MobileIcon name="tag" size={18} color="#fff" strokeWidth={1.9} />
              <span className="min-w-0 truncate">Journée promo · {b.promosToday.map((p) => p.name).join(", ")}</span>
            </div>
          )}

          <HeroCard
            label="CA de la boutique"
            ca={b.ca}
            prevCa={b.prevCa}
            delta={b.delta}
            cmpLabel={cmpDateLabel(period)}
            objectivePct={b.objectivePct}
            rows={[{ label: b.city, dot: type === "FRANCHISE" ? "oklch(0.78 0.1 300)" : "var(--hero-ring)", columns: ov.data.cmpColumns }]}
            tickets={b.tickets}
            panier={b.panier}
            marge={b.marge}
          />

          {ov.data.hourly ? (
            <HourlyBars key={barsKey} hourly={ov.data.hourly} mode="cmp" currentLabel={periodShortLabel(period)} cmpLabel={cmpDateLabel(period)} />
          ) : (
            <PeriodBars key={barsKey} chart={ov.data.chart} title={BARS_TITLE[period.period] ?? "Ventes"} currentLabel={periodShortLabel(period)} cmpLabel={cmpShort} />
          )}

          {today && ov.data.summary && <AiSummary text={ov.data.summary} />}

          {ov.data.networkSize > 1 && (
            <PositionCard items={ov.data.position} note={me.networkWide ? "Trait foncé : moyenne réseau" : "Trait foncé : moyenne de mes boutiques"} />
          )}

          <TopProductsCard
            products={ov.data.topProducts}
            cmpLabel={cmpShort}
            onSelect={(p) => openProduct({ id: p.id, name: p.name, scope: "boutique", boutiqueId: id })}
          />

          {upcoming.length > 0 && <UpcomingPromosGroup promos={upcoming.slice(0, 4)} />}
        </>
      )}
    </div>
  );
}
